import type { MemoryDecision, MemoryRecord, MemoryTrace } from './velis-memory'

import { array, literal, object, optional, picklist, pipe, safeParse, string, trim } from 'valibot'

import { applyMemoryDecision, memoryKinds, memoryStatuses } from './velis-memory'

export const webMemoryStorageKey = 'velis/web-memory'

interface MemoryStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
}

const recordSchema = array(object({
  characterId: optional(pipe(string(), trim())),
  content: pipe(string(), trim()),
  id: pipe(string(), trim()),
  kind: picklist(memoryKinds),
  provenance: object({
    recordedAt: pipe(string(), trim()),
    sessionId: pipe(string(), trim()),
    source: literal('user'),
  }),
  status: picklist(memoryStatuses),
  supersededById: optional(pipe(string(), trim())),
  supersedesId: optional(pipe(string(), trim())),
  topic: pipe(string(), trim()),
}))

const activitySchema = array(object({
  action: picklist(['WRITE', 'SUPERSEDE', 'FORGET', 'SKIP'] as const),
  error: optional(pipe(string(), trim())),
  memoryId: optional(pipe(string(), trim())),
  reason: pipe(string(), trim()),
  sessionId: pipe(string(), trim()),
  source: literal('user'),
}))

const fileSchema = object({
  activity: activitySchema,
  records: recordSchema,
})

function readFile(storage: MemoryStorage) {
  const raw = storage.getItem(webMemoryStorageKey)
  if (!raw)
    return { activity: [] as Array<MemoryTrace & { error?: string }>, records: [] as MemoryRecord[] }
  const parsed = safeParse(fileSchema, JSON.parse(raw))
  if (!parsed.success)
    return { activity: [], records: [], error: 'Velis web memory is invalid.' }
  return parsed.output
}

/** Browser-local memory. Desktop files stay on the Electron gateway. */
export function createWebMemoryGateway(storage: MemoryStorage) {
  return {
    async read() {
      try {
        const file = readFile(storage)
        return { records: file.records, error: 'error' in file ? file.error : undefined }
      }
      catch {
        return { records: [] as MemoryRecord[], error: 'Velis web memory cannot be read.' }
      }
    },
    async apply(decision: MemoryDecision) {
      try {
        const file = readFile(storage)
        if ('error' in file && file.error)
          return { error: file.error }
        const records = decision.trace.action === 'SKIP' ? file.records : applyMemoryDecision(file.records, decision)
        const activity = [...file.activity, decision.trace].slice(-8)
        storage.setItem(webMemoryStorageKey, JSON.stringify({ activity, records }))
        return {}
      }
      catch {
        return { error: 'Velis web memory cannot be written.' }
      }
    },
    async activity() {
      try {
        const file = readFile(storage)
        return { entries: [...file.activity].reverse(), error: 'error' in file ? file.error : undefined }
      }
      catch {
        return { entries: [], error: 'Velis web memory cannot be read.' }
      }
    },
  }
}
